import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * @param {string} query - The type of media query to use.
 *      'up' returns true if the screen width is greater than or equal to the start width.
 *      'down' returns true if the screen width is less than the start width.
 *      'between' returns true if the screen width is greater than or equal to the start width
 *      and less than or equal to the end width.
 *      'only' returns true if the screen width is equal to the start width.
 * @param {string} start - The starting width for the media query. Must be a valid MUI breakpoint.
 * @param {string} [end] - The ending width for the media query. Must be a valid MUI breakpoint.
 * @returns {boolean} - The result of the media query.
 */
export default function useResponsive(query, start, end) {
  const theme = useTheme();

  const mediaUp = useMediaQuery(theme.breakpoints.up(start));

  const mediaDown = useMediaQuery(theme.breakpoints.down(start));

  const mediaBetween = useMediaQuery(theme.breakpoints.between(start, end));

  const mediaOnly = useMediaQuery(theme.breakpoints.only(start));

  if (query === 'up') {
    return mediaUp;
  }

  if (query === 'down') {
    return mediaDown;
  }

  if (query === 'between') {
    return mediaBetween;
  }

  return mediaOnly;
}


/**
 * Returns the MUI breakpoint of the current screen width.
 * @returns {string} - The MUI breakpoint of the current screen width.
 */
export function useWidth() {
  const theme = useTheme();

  const keys = [...theme.breakpoints.keys].reverse();

  return (
    keys.reduce((output, key) => {
      const matches = useMediaQuery(theme.breakpoints.up(key));

      return !output && matches ? key : output;
    }, null) || 'xs'
  );
}
